import {
  COOKIE_AUTH_TOKEN,
  COOKIE_EXPIRES_TOKEN,
  COOKIE_REFRESH_TOKEN,
  FORMAT_DATE_TIME_DAYJS,
  NO_DATA,
  REMEMBER_LOGIN
} from 'constants/common'
import { DATE_INVALID, MONTHS, MONTH_HAS_31_DAY } from 'constants/date'
import dayjs, { Dayjs } from 'dayjs'
import { EUnitType } from 'enums'
import {
  getNumberCommasFixed,
  removeVietnameseTones
} from 'hooks/useFormatData'
import Cookies from 'js-cookie'
import moment from 'moment'

export function formatBytes(bytes: number, n = 2) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Byte'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return (
    Math.round((bytes / Math.pow(1024, i)) * Math.pow(10, n)) /
      Math.pow(10, n) +
    sizes[i]
  )
}

export function formatNumberToCurrency(numProps: number) {
  // remove sign if negative
  let sign = 1
  if (numProps < 0) {
    sign = -1
    numProps = -numProps
  }
  // trim the number decimal point if it exists
  const num = numProps.toString().includes('.')
    ? numProps.toString().split('.')[0]
    : numProps.toString()
  const len = num.toString().length
  let result = ''
  let count = 1

  for (let i = len - 1; i >= 0; i--) {
    result = num.toString()[i] + result
    if (count % 3 === 0 && count !== 0 && i !== 0) {
      result = ',' + result
    }
    count++
  }

  // add number after decimal point
  if (num.toString().includes('.')) {
    result = result + '.' + num.toString().split('.')[1]
  }
  // return result with - sign if negative
  return sign < 0 ? '-' + result : result
}

export function getAsPathApi(
  path: string,
  fields: { [key: string]: string | number }
) {
  return path
    .split('/')
    .map((el) => {
      if (el.match(/([{}])/)) {
        const route = el.slice(1, el.length - 1)
        if (fields.hasOwnProperty(route)) {
          return fields[route]
        }
      }
      return el
    })
    .join('/')
}

export const isValidSingleSlug = (str: string) => {
  const regexExp = /^[a-z0-9]+(?:-[a-z0-9]+)*$/g

  return regexExp.test(str)
}

export const clickAndOpenInNewTab = (url: string) => {
  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.click()
}

export const copyToClipboard = (str: string) => {
  if (navigator && navigator.clipboard && navigator.clipboard.writeText)
    return navigator.clipboard.writeText(str)
  return Promise.reject('The Clipboard API is not available.')
}

export const convertDayToTime = (day: Dayjs) => {
  const time = dayjs(day).format(FORMAT_DATE_TIME_DAYJS)
  const date = new Date(time)
  return date.getTime()
}

export const addDays = (date: Date, days: number) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

export const formatDate = (
  timeStamp: number | string | undefined | Date,
  format = 'DD/MM/YYYY'
) => {
  if (!timeStamp) return ''
  return moment(timeStamp).format(format)
}

export const getFirstDayOfMonth = (dateTime: any) => {
  const date = getBeginDate(new Date(dateTime))
  date.setDate(1)
  return date
}

export const getBeginDate = (dateTime: any) => {
  const date = new Date(dateTime)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
}

export const getEndDate = (dateTime: any) => {
  const date = new Date(dateTime)
  date.setHours(23)
  date.setMinutes(59)
  date.setSeconds(59)
  date.setMilliseconds(99)
  return date
}

export function camelToUnderscore(key: string) {
  const result = key.replace(/([A-Z])/g, ' $1')
  return result.split(' ').join('-').toLowerCase()
}

export const camelToKebabCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `-${letter?.toLowerCase()}`)

export const pascalToKebabCase = (str: string) => {
  const newString = str.replace(
    /[A-Z]/g,
    (letter) => `-${letter?.toLowerCase()}`
  )
  return newString.slice(1, 0) + newString.slice(1)
}

export const removeToken = () => {
  localStorage.removeItem(COOKIE_AUTH_TOKEN)
  localStorage.removeItem(COOKIE_EXPIRES_TOKEN)
  localStorage.removeItem(COOKIE_REFRESH_TOKEN)
  Cookies.remove(COOKIE_AUTH_TOKEN)
  Cookies.remove(COOKIE_EXPIRES_TOKEN)
  Cookies.remove(COOKIE_REFRESH_TOKEN)
}

export const saveToken = (response: any) => {
  const rememberLogin = !!localStorage.getItem(REMEMBER_LOGIN)
  if (rememberLogin) {
    Cookies.set(COOKIE_AUTH_TOKEN, response?.access_token, {
      expires: new Date(new Date().getTime() + 31700000000)
    })
    Cookies.set(COOKIE_REFRESH_TOKEN, response?.refresh_token, {
      expires: new Date(new Date().getTime() + 31700000000)
    })
    Cookies.set(COOKIE_EXPIRES_TOKEN, response?.access_token_expires_at, {
      expires: new Date(new Date().getTime() + 31700000000)
    })
  } else {
    Cookies.set(COOKIE_AUTH_TOKEN, response?.access_token)
    Cookies.set(COOKIE_REFRESH_TOKEN, response?.refresh_token)
    Cookies.set(COOKIE_EXPIRES_TOKEN, response?.access_token_expires_at)
  }
}

export const debounce = (
  fn: (...params: any[]) => any,
  n: number,
  immed = false
) => {
  let timer: any
  return function (this: any, ...args: any[]) {
    if (timer === undefined && immed) {
      fn.apply(this, args)
    }
    clearTimeout(timer)
    timer = setTimeout(() => fn.apply(this, args), n)
    return timer
  }
}

export function titleCase(str: string) {
  const splitStr = str.toLowerCase().split(' ')
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
  }
  return splitStr.join(' ')
}

export const removeFieldsNull = (object: any) => {
  const result: any = {}
  Object.keys(object).forEach((key) => {
    if (object[key] !== undefined && object[key] !== null) {
      result[key] = object[key]
    }
  })
  return result
}

export const convertImgToBase64 = (url: string, outputFormat?: string) => {
  return new Promise((resolve: any) => {
    let canvas: any = document.createElement('CANVAS')
    const ctx = canvas.getContext('2d')
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = function () {
      canvas.height = img.height
      canvas.width = img.width
      ctx.drawImage(img, 0, 0)
      const dataURL = canvas.toDataURL(outputFormat || 'image/png')
      resolve(dataURL)
      canvas = null
    }
    img.src = url
  })
}

export const getDaysOfMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate()
}

export const getTimeToday = () => {
  const firstDay = getBeginDate(new Date())
  const toDate = getBeginDate(new Date())
  toDate.setDate(toDate.getDate() + 1)
  return {
    from: firstDay.getTime(),
    to: toDate.getTime() - 1
  }
}

export const getTimeWeek = () => {
  const firstDay = getBeginDate(new Date())
  firstDay.setDate(firstDay.getDate() - (firstDay.getDay() || 6))

  const toDay = getBeginDate(new Date())
  toDay.setDate(toDay.getDate() + (7 - (toDay.getDay() || 6)))
  return { from: firstDay.getTime(), to: toDay.getTime() - 1 }
}

export const getTimeMonth = () => {
  const firstDay = getBeginDate(new Date())
  firstDay.setDate(1)
  const toDay = getBeginDate(new Date())
  toDay.setDate(1)
  toDay.setMonth(toDay.getMonth() + 1)
  return { from: firstDay.getTime(), to: toDay.getTime() - 1 }
}

export const getOld = (birth: string | Date) => {
  return new Date().getFullYear() - new Date(birth).getFullYear()
}

export const getOldFromYear = (year?: number) => {
  if (!year) return undefined
  return new Date().getFullYear() - year
}

function convertBlockThree(number: string) {
  const defaultNumbers = ' hai ba bốn năm sáu bảy tám chín'
  const chuHangDonVi = ('1 một' + defaultNumbers).split(' ')
  const chuHangTram = ('không một' + defaultNumbers).split(' ')

  if (number === '000') return ''
  const _a = number + '' //Convert biến 'number' thành kiểu string

  //Kiểm tra độ dài của khối
  switch (_a.length) {
    case 0:
      return ''
    case 1:
      return chuHangDonVi[_a as any]
    case 2:
      return convertBlockTwo(_a)
    case 3:
      let chuc_dv = ''
      if (_a.slice(1, 3) !== '00') {
        chuc_dv = convertBlockTwo(_a.slice(1, 3))
      }
      const tram = chuHangTram[_a[0] as any] + ' trăm'
      return tram + ' ' + chuc_dv
  }
}

function convertBlockTwo(number: any) {
  const defaultNumbers = ' hai ba bốn năm sáu bảy tám chín'
  const chuHangDonVi = ('1 một' + defaultNumbers).split(' ')
  const chuHangChuc = ('lẻ mười' + defaultNumbers).split(' ')
  let dv = chuHangDonVi[number[1]]
  const chuc = chuHangChuc[number[0]]
  let append = ''

  // Nếu chữ số hàng đơn vị là 5
  if (number[0] > 0 && number[1] === 5) {
    dv = 'lăm'
  }

  // Nếu số hàng chục lớn hơn 1
  if (number[0] > 1) {
    append = ' mươi'

    if (number[1] === 1) {
      dv = ' mốt'
    }
  }

  return chuc + '' + append + ' ' + dv
}

export function convertMoneyWords(number: string | number) {
  const dvBlock = '1 nghìn triệu tỷ'.split(' ')
  const str = parseInt(number?.toString()) + ''
  let i = 0
  const arr = []
  let index = str.length
  const result = []
  let rsString = ''

  if (index === 0 || str === 'NaN') {
    return ''
  }

  // Chia chuỗi số thành một mảng từng khối có 3 chữ số
  while (index >= 0) {
    arr.push(str.substring(index, Math.max(index - 3, 0)))
    index -= 3
  }

  // Lặp từng khối trong mảng trên và convert từng khối đấy ra chữ Việt Nam
  for (i = arr.length - 1; i >= 0; i--) {
    if (arr[i] !== '' && arr[i] !== '000') {
      result.push(convertBlockThree(arr[i]))

      // Thêm đuôi của mỗi khối
      if (dvBlock[i]) {
        result.push(dvBlock[i])
      }
    }
  }

  // Join mảng kết quả lại thành chuỗi string
  rsString = result.join(' ')

  // Trả về kết quả kèm xóa những ký tự thừa
  return capitalizeFirstLetter(
    rsString.replace(/[0-9]/g, '').replace(/ /g, ' ').replace(/ $/, '')
  )
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function getFromToDate(
  date: Date,
  fromTo: Array<Date>
): { from: number; to: number } | undefined {
  if (fromTo?.[0]) {
    const [from, to] = fromTo
    const fromDate = new Date(date)
    const toDate = new Date(date)
    fromDate.setHours(
      new Date(from)?.getHours(),
      new Date(from).getMinutes(),
      0
    )
    toDate.setHours(new Date(to).getHours(), new Date(to).getMinutes(), 0)
    return { from: fromDate?.getTime(), to: toDate?.getTime() }
  }
}

export function getDateWithTime(date?: Date, time?: Date): Date | undefined {
  if (time && date) {
    const newDate = new Date(date)
    newDate.setHours(new Date(time)?.getHours(), new Date(time).getMinutes(), 0)
    return newDate
  }
  return undefined
}

export function getDateAddMinute(date: Date, minute: number): Date {
  const newDate = new Date(date)
  newDate.setMinutes(newDate.getMinutes() + minute)
  return newDate
}

export function compareSameDate(dateA: any, dateB: any) {
  return (
    formatDate(new Date(dateA), 'DD/MM/YYYY') ===
    formatDate(new Date(dateB), 'DD/MM/YYYY')
  )
}
export const generateRandomPassword = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let password = ''
  while (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/.test(password)) {
    password = Array(8)
      .fill(null)
      .map(() => chars.charAt(Math.floor(Math.random() * chars.length)))
      .join('')
  }
  return password
}
export function getGuiId() {
  const u =
    Date.now().toString(16) + Math.random().toString(16) + '0'.repeat(16)
  return [
    u.substr(0, 8),
    u.substr(8, 4),
    '4000-8' + u.substr(13, 3),
    u.substr(16, 12)
  ].join('-')
}

export function getCurrentDateWithStep(step: number): Date {
  const currentDate: Date = new Date()
  const minutes: number = currentDate.getMinutes()
  let roundedMinutes: number = Math.ceil(minutes / step) * step

  if (roundedMinutes >= 60) {
    currentDate.setHours(currentDate.getHours() + 1)
    roundedMinutes = 0
  }

  currentDate.setMinutes(roundedMinutes, 0, 0)

  if (currentDate.getHours() === 23 && minutes > 30) {
    currentDate.setDate(currentDate.getDate() + 1)
    currentDate.setHours(0, 0, 0, 0)
  }
  return currentDate
}

export function convertImageToBase64(
  imageUrl: string,
  callback: (base64Data: string) => void
) {
  const xhr = new XMLHttpRequest()
  xhr.onload = function () {
    const reader = new FileReader()
    reader.onloadend = function () {
      const base64Data = reader.result as string
      callback(base64Data)
    }
    reader.readAsDataURL(xhr.response)
  }
  xhr.open('GET', imageUrl)
  xhr.responseType = 'blob'
  xhr.send()
}

export const timeDelay = (delayInms: number) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms))
}

export const getPriceFromPoint = (point?: number) => {
  if (!point) return point
  return point * 1000
}
export const getPriceFromPointComma = (point?: number) => {
  if (!point) return point
  return getNumberCommasFixed(point * 1000)
}

export const getNumberComaPriceFromPoint = (point?: number) => {
  if (!point) return point
  return getNumberCommasFixed(point * 1000)
}
export const checkIsUnitVien = (unit?: string) => {
  if (!unit) return undefined
  const isUnitVien =
    removeVietnameseTones(unit).toLowerCase() === EUnitType.Vien
  return isUnitVien
}

export function calculateDaysDifference(date1: string, date2: string): number {
  // Convert date strings to Date objects
  const parsedDate1 = new Date(date1)
  const parsedDate2 = new Date(date2)

  // Calculate the difference in milliseconds
  const timeDifference = parsedDate2.getTime() - parsedDate1.getTime()

  // Convert the difference from milliseconds to days
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24)

  return daysDifference
}

export function capitalizeName(input: string): string {
  // Tách chuỗi thành các từ
  const words = input.split(' ')

  // Chuyển đổi mỗi từ thành dạng viết hoa chữ cái đầu
  const capitalizedWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1)
  )

  // Kết hợp các từ đã chuyển đổi thành chuỗi mới
  const result = capitalizedWords.join(' ')
  return result
}

export const renderDateString = (date: string) => {
  if (date === DATE_INVALID) return NO_DATA
  if (!date) return NO_DATA
  return date
}

export const getYearBirthdayOptions = (
  yearAvailable: number,
  month?: number,
  date?: number
) => {
  const isLeapYear = month === 2 && date === 29
  const currentYear = Number(moment(new Date()).format('YYYY'))
  const optionsYear = []
  for (let index = 0; index < yearAvailable; index++) {
    if (isLeapYear) {
      if (checkLeapYear(currentYear - index)) {
        optionsYear.push({
          label: currentYear - index,
          value: currentYear - index
        })
      }
    } else {
      optionsYear.push({
        label: currentYear - index,
        value: currentYear - index
      })
    }
  }
  return optionsYear
}

export const getMonthOptions = (date: number, year: number) => {
  switch (date) {
    case 31:
      return MONTH_HAS_31_DAY?.map((item) => ({ label: item, value: item }))
    case 30:
      return MONTHS?.filter((m) => m !== 2)?.map((month) => ({
        label: month,
        value: month
      }))
    case 29:
      if (!year || checkLeapYear(year)) {
        return MONTHS.map((month) => ({
          label: month,
          value: month
        }))
      }
      return MONTHS?.filter((m) => m !== 2)?.map((month) => ({
        label: month,
        value: month
      }))

    default:
      return MONTHS?.map((month) => ({ label: month, value: month }))
  }
}

export const getDayOptions = (month: number, year: number) => {
  let totalDayInMonth = 30
  if (MONTH_HAS_31_DAY.includes(month)) {
    totalDayInMonth = 31
  }
  if (month === 2) {
    const isLeapYear = checkLeapYear(year)
    if (isLeapYear || !year) {
      totalDayInMonth = 29
    } else {
      totalDayInMonth = 28
    }
  }
  if (!month) {
    totalDayInMonth = 31
  }
  const optionsYear = []
  for (let index = 1; index <= totalDayInMonth; index++) {
    optionsYear.push({
      label: index,
      value: index
    })
  }
  return optionsYear
}

export function checkLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function getDateOfBirthString(
  date?: string | number,
  month?: string | number,
  year?: string | number
) {
  if (date && date?.toString().length === 1) {
    date = `0${date}`
  }
  if (month && month?.toString().length === 1) {
    month = `0${month}`
  }
  let dateString = `${date || NO_DATA}/`
  dateString += `${month || NO_DATA}/`
  dateString += `${year || NO_DATA}`
  return dateString
}

export const convertTimeSecondsToMinuteSeconds = (count: number): string => {
  const hours: string | number = Math.floor(count / 3600)
  let minutes: string | number = Math.floor(count / 60) - hours * 60
  let seconds: string | number = parseFloat((count % 60).toFixed(0))

  if (minutes < 10) minutes = `0${minutes}`
  if (seconds < 10) seconds = `0${seconds}`

  return `${minutes}:${seconds}`
}

export const getOldPatient = (year?: number) => {
  const old = getOldFromYear(year)
  if (old === 0) return '<1'
  return old
}

export const getDateFromAddYear = (addYear: number) => {
  const date = new Date()
  const year = date.getFullYear()
  date.setFullYear(year + addYear)
  return date
}

export function cutWords(str: string, length: number): string[] {
  const result: string[] = []

  while (str.length > length) {
    const cutIndex: number = str.lastIndexOf(' ', length)
    if (cutIndex === -1) {
      result.push(str.slice(0, length))
      str = str.slice(length)
    } else {
      result.push(str.slice(0, cutIndex))
      str = str.slice(cutIndex + 1)
    }
  }

  if (str.length > 0) {
    result.push(str)
  }

  return result
}

export const convertExcelDate = (excelDate: number) => {
  const date = new Date(
    (excelDate - 1) * 86400000 + new Date(1900, 0, 1).getTime()
  )
  return new Date(date).getTime()
}
export const getKeyNote = (keyName: string) => {
  return `${keyName}Note`
}
